import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Box, Button, Container, Typography, Stack, Avatar, Chip, CircularProgress, Alert } from "@mui/material";
import MatchApi, { MatchResults } from "../api/MatchApi";
import { eur } from "../constants/funnel";

export default function Results() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const language = (location.state as any)?.language;
  const [data, setData] = useState<MatchResults | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!id) return;
    MatchApi.results(id, language).then(setData).catch(() => setError(true));
  }, [id, language]);

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: "center" }}>
        <Typography variant="h2" sx={{ mb: 2 }}>{t("results.error")}</Typography>
        <Button variant="contained" onClick={() => navigate("/start")}>{t("common.retry")}</Button>
      </Container>
    );
  }
  if (!data) {
    return (
      <Box sx={{ minHeight: "60dvh", display: "grid", placeItems: "center" }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress sx={{ color: "primary.main" }} />
          <Typography sx={{ color: "text.secondary" }}>{t("results.loading")}</Typography>
        </Stack>
      </Box>
    );
  }

  const r = data.reimbursement;

  return (
    <Box sx={{ bgcolor: "background.default", pb: 10 }}>
      <Container maxWidth="md" sx={{ pt: { xs: 5, sm: 8 } }}>
        <Typography variant="caption" sx={{ color: "secondary.dark", fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase" }}>
          {t("results.advice")}
        </Typography>
        <Typography variant="h1" sx={{ fontSize: { xs: "2rem", sm: "2.75rem" }, mt: 1, mb: 1 }}>
          {t(`recommendation.${data.recommendation}.title`)}
        </Typography>
        <Typography sx={{ fontSize: "1.1rem", maxWidth: 560 }}>{t(`recommendation.${data.recommendation}.blurb`)}</Typography>

        {data.recommendation === "self_help" && (
          <Button variant="contained" sx={{ mt: 3 }} onClick={() => navigate("/zelfhulp")}>{t("results.selfHelpCta")}</Button>
        )}

        {r && r.perSessionCents > 0 && !r.notEligible && (
          <Box sx={{ mt: 4, p: 3, borderRadius: "var(--radius-lg)", bgcolor: "var(--primary-50)", border: "1px solid var(--primary-100)" }}>
            <Typography sx={{ color: "primary.dark", m: 0 }}>
              <Trans
                i18nKey="results.reimbursement"
                values={{
                  fund: r.fundName,
                  amount: eur(r.perSessionCents),
                  youPay: eur(r.youPayCents),
                  cap: r.maxSessionsPerYear ? t("results.reimbursementCap", { count: r.maxSessionsPerYear }) : "",
                }}
                components={[<strong />, <strong />, <strong />]}
              />
            </Typography>
          </Box>
        )}
        {r && r.notEligible && (
          r.fundCode === "cm" ? (
            <Alert severity="warning" sx={{ mt: 4 }}>{t("vetting.cmWarningClient")}</Alert>
          ) : (
            <Box sx={{ mt: 4, p: 2.5, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface-alt)", border: "1px solid var(--border)" }}>
              <Typography sx={{ color: "text.secondary", m: 0, fontSize: ".95rem" }}>{t("vetting.notEligibleFund", { fund: r.fundName })}</Typography>
            </Box>
          )
        )}

        <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, flexWrap: "wrap", mt: 6, mb: 3 }}>
          <Typography variant="h2" sx={{ fontSize: "1.5rem", m: 0 }}>
            {data.psychologists.length ? t("results.matchedTitle") : t("results.emptyTitle")}
          </Typography>
          {/* VI.99 §6 WER: link the ranking-transparency page from the results. */}
          <Box component={RouterLink} to="/hoe-matchen-wij" sx={{ fontSize: ".9rem", color: "var(--link)", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
            {t("results.howWeMatch")}
          </Box>
        </Box>

        {data.psychologists.length === 0 ? (
          <Box sx={{ p: 4, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", textAlign: "center" }}>
            <Typography sx={{ mb: 2 }}>{t("results.emptyBody")}</Typography>
            <Button variant="contained" onClick={() => navigate("/")}>{t("common.backHome")}</Button>
          </Box>
        ) : (
          <Stack spacing={2}>
            {data.psychologists.map((p) => (
              <Box key={p.id} sx={{ display: "flex", gap: 2.5, p: 3, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)" }}>
                <Avatar src={p.photoUrl} sx={{ width: 64, height: 64, bgcolor: "var(--primary-100)", color: "primary.dark", fontFamily: "var(--font-display)" }}>
                  {p.displayName?.[0]}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h3" sx={{ fontSize: "1.2rem", mb: 0.5 }}>{p.displayName}</Typography>
                  {p.bio && <Typography sx={{ fontSize: ".95rem", mb: 1 }}>{p.bio}</Typography>}
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                    {p.specializations.slice(0, 3).map((s) => (
                      <Chip key={s} size="small" label={t(`topics.${s}.label`)} sx={{ bgcolor: "var(--primary-50)", color: "primary.dark" }} />
                    ))}
                  </Stack>
                  {r && r.fundCode !== "none" && p.eligibleFundCodes && !p.eligibleFundCodes.includes(r.fundCode) && (
                    r.fundCode === "cm" ? (
                      <Alert severity="warning" sx={{ mb: 1, py: 0 }}>{t("vetting.cmWarningClientShort")}</Alert>
                    ) : (
                      <Typography sx={{ fontSize: ".82rem", color: "text.secondary", mb: 1 }}>{t("vetting.notEligibleFundShort", { fund: r.fundName })}</Typography>
                    )
                  )}
                  <Button variant="contained" size="small" onClick={() => navigate(`/boek/${p.id}`)}>{t("results.book")}</Button>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Container>
    </Box>
  );
}
